import { useEffect, useMemo, useRef, useState } from "react";

import * as Material from "@mui/material";

import BottomSubmit from "../../components/BottomSubmit";
import ConfigLoader from "../../components/ConfigLoader";

import "./styles.css";

import api from "@helpers/api";

import { Text, Button, HelpdeskSupport } from "@fragments";

import { IconTrendingUp, IconArrowFork } from "@tabler/icons-react";

import { useCommunity } from "contexts/community";
import { useAuth } from "contexts/auth";

import { useParams } from "react-router-dom";

import ManageCouponModal from "@modals/Billing/ManageCouponModal";

import SplitFlow from "./components/SplitFlow";
import SplitCurrencyNotice from "./components/SplitCurrencyNotice";
import InviteReceiverModal from "./components/InviteReceiverModal";
import { projectSplitAcceptance } from "./helpers/projectSplitAcceptance";

const SPLIT_HELPDESK_ARTICLES = [
  {
    title: "Como configurar split de receita",
    subtitle: "Aprenda a dividir a receita entre recebedores.",
    url: "https://help.ginastee.com/pt-br/split-e-mentoria/como-configurar-split-de-receita",
    icon: <IconArrowFork />,
  },
  {
    title: "Como mensurar os resultados da sua comunidade",
    subtitle:
      "Descubra os recursos que o Ginastee oferece para trackeamento de resultados",
    icon: <IconTrendingUp />,
    url: "https://help.ginastee.com/pt-br/relatorios-pixels-e-integracoes/como-acompanhar-os-resultados-da-sua-comunidade",
  },
];

export default function Split({ info }) {
  // Active receivers (config.customer — must sum to 100%) and pending split
  // invitations (not summed, never sent in the PUT payload) are kept apart.
  const [receivers, setReceivers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();
  const [hasUpdates, setHasUpdates] = useState(false);

  let { slug } = useParams();

  const { loading: communityLoading, community } = useCommunity();
  const { user } = useAuth();

  const currentUserEmail = user?.email?.address || user?.email || null;

  const REF_inviteReceiver = useRef();
  const REF_configCouponModal = useRef();

  useEffect(() => {
    if (!communityLoading) handleLoad();
  }, [communityLoading]);

  const currentCommunity = community.getBySlug(slug);

  async function handleLoad() {
    if (!currentCommunity?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        "community/" + currentCommunity.id + "/split",
      );
      setReceivers([...(data?.config?.customer ?? [])]);

      // Pending invitations may be embedded in GET /split (contract decision
      // #5 default) — but the documented `Split` schema doesn't include them,
      // so fall back to the standalone list endpoint when the embed is absent.
      if (Array.isArray(data?.invitations)) {
        setInvitations([...data.invitations]);
      } else {
        const res = await api.get(
          "community/" + currentCommunity.id + "/split/invitations",
        );
        const list = Array.isArray(res?.data)
          ? res.data
          : (res?.data?.invitations ?? []);
        setInvitations([...list]);
      }
    } catch (e) {
      console.log(e);
    }
    setLoading(false);
  }

  function handleInput(value, user) {
    // Mentoring shares are managed by the Mentoring module and are read-only
    // here (the API rejects edited mentoring percentiles).
    if (user?.type === "mentoring") return;

    const dataCopy = [...receivers].map((receiver) => {
      if (receiver.id == user.id) {
        receiver.percentile = parseInt(value);
      }
      return receiver;
    });
    setReceivers([...dataCopy]);
    setHasUpdates(true);
  }

  function handleRemoveReceiver(user) {
    const removeNow = () => {
      setReceivers([...receivers].filter((u) => u.id !== user.id));
      setHasUpdates(true);
    };

    // Removing an accepted coproduction receiver notifies them by e-mail
    // (backend UpdateSplitAction removal-diff). Confirm first — but the
    // user removing their own entry needs no warning.
    const email = (user?.email?.address || user?.email || "").toLowerCase();
    const isSelf =
      currentUserEmail && email === String(currentUserEmail).toLowerCase();

    if (isSelf || !window.openConfirm) {
      removeNow();
      return;
    }

    window.openConfirm({
      title: "Remover recebedor",
      headline: "Remover este recebedor do split?",
      subtitle:
        "Ao salvar, o recebedor será removido e notificado por e-mail. As demais cotas serão renormalizadas.",
      buttons: [
        {
          text: "Remover recebedor",
          variant: "contained",
          color: "error",
          onClick: removeNow,
        },
        { text: "Cancelar", variant: "text" },
      ],
    });
  }

  async function handleRevokeInvitation(invitation) {
    const doRevoke = async () => {
      const res = await api.delete(
        "community/" +
          currentCommunity.id +
          "/split/invitations/" +
          invitation.id,
      );
      if (res?.response?.status >= 400) {
        window.setSnackbar?.(
          res.response.data?.message || "Não foi possível revogar o convite",
        );
        return;
      }
      window.setSnackbar?.("Convite revogado");
      handleLoad();
    };

    if (!window.openConfirm) {
      doRevoke();
      return;
    }

    window.openConfirm({
      title: "Revogar convite",
      headline: "Revogar este convite?",
      subtitle:
        "O convidado não poderá mais aceitar este convite. Esta ação não pode ser desfeita.",
      buttons: [
        {
          text: "Revogar convite",
          variant: "contained",
          color: "error",
          onClick: doRevoke,
        },
        { text: "Cancelar", variant: "text" },
      ],
    });
  }

  async function handleSubmit() {
    try {
      if (!currentCommunity?.id) return;

      const valid = isValid();

      if (!valid) {
        return;
      }

      const config = receivers.map((v) => {
        // Mentoring entries are owned by the Mentoring module: the API
        // requires every one to be echoed back with its hashed
        // enrollment id and an unchanged percentile, so send them as-is.
        if (v.type === "mentoring") {
          return {
            user: v.id,
            percentile: v.percentile,
            type: "mentoring",
            mentoring_enrollment_id: v.mentoring_enrollment_id,
          };
        }

        return {
          user: v.id,
          percentile: parseInt(v.percentile, 10),
        };
      });

      const response = await api.put(
        "community/" + currentCommunity.id + "/split",
        { config },
      );

      if (response?.data?.id) {
        window.setSnackbar("Regras de split salvas com sucesso");
        setHasUpdates(false);
      } else {
        setError(
          response?.response?.data?.message ||
            "Um erro ocorreu ao salvar as regras de split",
        );
      }
    } catch (e) {
      setError("Houve um erro ao salvar as regras de split");
      console.log(e);
    }

    function isValid() {
      const allValuesValid =
        [...receivers].filter((v) => v.percentile <= 0 || v.percentile > 100)
          .length === 0;
      if (!allValuesValid) {
        setError("Insira um percentual entre 0 e 100");
        return;
      }

      const sum = [...receivers].reduce(
        (n, { percentile }) => n + Number(percentile || 0),
        0,
      );

      // Mirror the backend tolerance (CustomerConfigSumRule: |sum - 100| < 0.0001)
      // so the client never green-lights a payload the API will 422.
      if (Math.abs(sum - 100) >= 0.0001) {
        setError("A soma dos valores de split deve ser igual a 100");
        return;
      }

      return true;
    }
  }

  // Room left for a new invite = the owner's residual: total minus the fixed
  // mentoring shares, the other (fixed) coproducers, and the shares already
  // promised to other open invites. Only the owner gives up share.
  function availablePercentile() {
    const ownerEmail = String(currentUserEmail || "").toLowerCase();
    let mentoringTotal = 0;
    let nonOwnerCoproducerTotal = 0;
    for (const r of receivers) {
      const pct = Number(r.percentile || 0);
      if (r.type === "mentoring") {
        mentoringTotal += pct;
      } else {
        const email = String(r?.email?.address || r?.email || "").toLowerCase();
        if (email !== ownerEmail) nonOwnerCoproducerTotal += pct;
      }
    }
    const openInviteTotal = invitations
      .filter((i) => ["pending", "awaiting_wallet"].includes(i.status))
      .reduce((sum, i) => sum + Number(i.percentile || 0), 0);
    return Math.max(
      0,
      Math.round(
        (100 - mentoringTotal - nonOwnerCoproducerTotal - openInviteTotal) *
          100,
      ) / 100,
    );
  }

  function handleAddReceiver() {
    const available = availablePercentile();
    if (available < 0.01) {
      window.setSnackbar?.(
        "Não há percentual disponível para novos recebedores",
      );
      return;
    }
    REF_inviteReceiver.current.open(currentCommunity.id, available, handleLoad);
  }

  function ErrorMessage() {
    useEffect(() => {
      if (error) {
        setTimeout(() => {
          setError(null);
        }, 4000);
      }
    }, [error]);
    return (
      <>
        {error && (
          <Material.Alert className="mt-2 error-form-split" severity="error">
            {error}
          </Material.Alert>
        )}
      </>
    );
  }

  const MEMO_bottomControl = useMemo(
    () => <BottomSubmit onPress={handleSubmit} key={hasUpdates} />,
    [hasUpdates],
  );

  // Preview what each active receiver's share becomes once the pending invite(s)
  // are accepted (mentoring + other coproducers stay static; only the owner's
  // residual absorbs them). Mirrors the backend AddSplitCoproducerEntryAction —
  // see helpers/projectSplitAcceptance. The editor is the community owner, so
  // currentUserEmail identifies the residual entry.
  const projection = useMemo(
    () => projectSplitAcceptance(receivers, invitations, currentUserEmail),
    [receivers, invitations, currentUserEmail],
  );

  return (
    <div className="config-page config-general-community" key={loading}>
      {loading || communityLoading ? (
        <ConfigLoader />
      ) : (
        <div className="mt-8">
          <Text.h3
            grow
            left={<IconArrowFork />}
            value="Configuração de split"
            className="mb-1"
          />
          <Text.h4
            value="Divida a receita das vendas do produto entre diferentes recebedores"
            contrast={5}
            className="mb-2 pl-4"
            style={{ transform: "translateX(7px)" }}
          />

          <div className="mt-2 pb-5">
            {/* International (Stripe) sales are not split — SplitCurrencyNotice
                below states the rule; the old escrow warning is gone with it. */}
            <SplitFlow
              receivers={receivers}
              invitations={invitations}
              projection={projection}
              currentUserEmail={currentUserEmail}
              onChangePercentile={(user, value) => handleInput(value, user)}
              onRemoveReceiver={handleRemoveReceiver}
              onRevokeInvitation={handleRevokeInvitation}
              onAddReceiver={handleAddReceiver}
            />
            <SplitCurrencyNotice />
          </div>

          <ErrorMessage />

          <HelpdeskSupport.CardsGroup cards={SPLIT_HELPDESK_ARTICLES} />
        </div>
      )}

      {hasUpdates ? MEMO_bottomControl : null}

      <InviteReceiverModal modalRef={REF_inviteReceiver} />

      <ManageCouponModal modalRef={REF_configCouponModal} />
    </div>
  );
}

/*
function SplitConfigModal({ modalRef }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const [error, setError] = useState();

    const [data, setData] = useState([])

    const REF_config = useRef({});
    const REF_callback = useRef();

    const REF_userSelect = useRef()

    modalRef.current = {
        open(communityId, callback = () => { }) {
            REF_config.current = { community: communityId };
            REF_callback.current = callback;
            setData([])
            setError(null);
            setOpen(true);
        },
    };



    async function loadSplitRules() {
        setLoading(true)
        try {
            console.log(REF_config.current)
            const { data } = await api.get('split?community=' + REF_config.current.community)
            setData([...data.config.customer])
        } catch (e) {
            console.log(e)
        }
        setLoading(false)
    }

    return (
        <>

            <CustomModal
                maxWidth={480}
                bottomButtonCancelText="Cancelar"
                loading={loading}
                title="Configurar regras de split"
                open={open}
                handleSubmit={handleSubmit}
                bottomButtonSumbitText="Salvar configuração"
                handleClose={() => setOpen(false)}
                bodyComponent={
                    open ? (
                        <div className="px-4 pt-4">

                        </div>
                    ) : null
                }
            />
        </>
    );
}*/
